<template>
  <section>
    <h1 class="visually-hidden">
      {{ pageHeading }}
    </h1>
    <div class="p-search__search">
      <div class="p-search__search-box">
        <RadAdvancedSearchBox
          ref="advancedSearchBox"
          :autocomplete-options="{
            minChars: 1,
            allowAnyInput: true,
            includeQueryDuplicatesMatchedValue: true,
            addSuffix: addSuffix,
            additionalNormalization: additionalNormalization,
            sortResults: (a, b) => {
              if (useAlphabeticTypeaheadSort == false) {
                return 0;
              }
              return a.match_string.localeCompare(b.match_string);
            }
          }"
          :autocomplete-values="suggestions"
          :add-suffix="addSuffix"
          :chip-terms="chipTerms"
          :class="classes()"
          :display-chips="!isMobile"
          :input-data="searchInputText"
          :intercept-autocomplete-event="true"
          :use-keyword-chips="false"
          @autocomplete-entry="autocompleteEntry"
          @get-autocomplete-values="
            async ($event) => {
              suggestions = await fetchSuggestions($event);
            }
          "
          @update-search="updateSearch($event, $router)"
        />
      </div>
    </div>
    <FacetExplorer
      ref="facetExplorerWrapper"
      class="p-search__facet-explorer"
      :heading-level="2"
      :active-tab="activeTab"
      :active-tab-data="activeFacets"
      :chip-terms="chipTerms"
      :facet-info="mobileFacetInfo ? mobileFacetInfo : facetInfo"
      :filter-info="filterInfo"
      :header-visibility="isHeaderVisible"
      :is-empty-search="isEmptySearch"
      :max-facets="maxFacets"
      :search-hash="searchHash"
      :selected-values="searchParams.filters"
      :total-results="total"
      @keypress-change="updateActiveTabFilters"
      @update-search="updateSearch($event, $router)"
      @update-tab-data="fetchActiveTabData"
    />
    <div
      v-if="displayError && displayErrorMessage"
      ref="search-error-message"
      class="error-message"
    >
      {{ translate(displayErrorMessage) }}
    </div>

    <!-- section primary content transition -->
    <Transition name="p-search__search-results" mode="out-in">
      <!-- loading state grid -->
      <GridOfItems
        v-if="isLoading == true"
        key="loadingResults"
        class="p-search__search-results--loading"
        :items="loadingItemsData"
        :max-items-per-row="6"
        :heading="translatePart('search.loading_aria_heading')"
        :show-heading="false"
      >
        <template #item>
          <LoadingImageAndCaption />
        </template>
      </GridOfItems>
      <!-- search results grid -->
      <div
        v-else-if="isEmptySearch == false && total > 0 && results"
        class="p-search__search-results"
        data-testid="search-results"
      >
        <GridOfItems
          key="searchResults"
          :items="results.data"
          :heading="translate('search.results_aria_heading')"
          :max-items-per-row="6"
          :show-heading="false"
        >
          <template #item="{ item }">
            <ImageAndCaption
              v-bind="toGridItem(item)"
              data-testid="search-results-item"
            >
              <template #afterMetadata>
                <SearchCartouches :item="item" />
              </template>
            </ImageAndCaption>
          </template>
        </GridOfItems>
        <PaginationBar
          class="m-search-view-options__info-column"
          :current-page="currentPage"
          :detailed="true"
          :size-value="searchParams.size"
          :from-value="searchParams.from"
          :total-hits="total"
          :max-visible-buttons="5"
          @update-search="updateSearch($event, $router)"
        />
      </div>
      <!-- empty search -->
      <SuggestedSearches
        v-else
        :query-terms="searchParams.query"
        :has-results="total > 0"
      />
    </Transition>
    <ContentDisclaimerAndUse />
  </section>
</template>

<script>
import {
  RadAdvancedSearchBox,
  PaginationBar,
  FacetExplorer,
  useLocalization,
  breakpointsMixin,
  ImageAndCaption,
  GridOfItems
} from "@thegetty-private/getty-ui";

import SearchCartouches from "@/components/atoms/searchCartouches/index.vue";

import LoadingImageAndCaption from "@/components/atoms/loadingImageAndCaption/index.vue";
import SuggestedSearches from "@/components/atoms/suggestedSearches/index.vue";
import ContentDisclaimerAndUse from "@/components/atoms/contentDisclaimerAndUse/index.vue";
import {
  searchService,
  useAutocomplete,
  BaseSearchPage as baseSearchPage
} from "@thegetty-private/rad-app-utils";

import mobileMixin from "@/mixins/mobileMixin/index.js";
import * as autocompleteHelpers from "@/helpers/autocomplete";
import * as configHelper from "@/helpers/configHelper";
import * as searchHelpers from "@/helpers/Search";
import { transformSearchResultToThumbnailItemWithArtist } from "@/helpers/SearchItemHelper";

import { ref } from "vue";
import { useHead } from "@unhead/vue";

import displayConstants from "@/data/display";
import filterMapConstants from "@/data/filter-map";

const metaTitle = ref(null);

export default {
  name: "SearchPage",
  components: {
    ContentDisclaimerAndUse,
    SearchCartouches,
    GridOfItems,
    ImageAndCaption,
    LoadingImageAndCaption,
    PaginationBar,
    RadAdvancedSearchBox,
    FacetExplorer,
    SuggestedSearches
  },
  extends: baseSearchPage,
  mixins: [breakpointsMixin, mobileMixin],
  inject: ["updatePageTitle"],
  setup(props) {
    return {
      ...useAutocomplete(),
      ...useLocalization({
        baseLocaleKey: props?.baseLocaleKey
      })
    };
  },
  data() {
    return {
      /**
       * Array of parameter objects used to render and disable tabs in facetExplorer
       */
      facetInfo: [
        {
          fieldName: "artist"
        },
        {
          fieldName: "decade_range",
          type: "DateRange",
          queryFieldName: "date_range"
        },
        { fieldName: "materials_parsed" },
        { fieldName: "culture" },
        { fieldName: "classification_and_object_type" }
      ],
      /**
       * Array of objects for updating state of filterbar checkboxes
       * [{ fieldName: "filter", disabled: false, checked: true, secondary: true}]
       */
      filterInfo: [
        { fieldName: "on_view" },
        { fieldName: "open_content" },
        { fieldName: "images" },
        { fieldName: "is_standalone", secondary: true },
        { fieldName: "include_deaccessioned", secondary: true }
      ],
      /**
       * Filter map constants object
       */
      filterMap: filterMapConstants,
      /**
       * Filters that require translation
       */
      filterTranslationKeys: ["artist"],
      /**
       * The page size or number of hits to return in each search
       */
      pageSize: displayConstants.PAGE_SIZE,
      /**
       * Array of fieldnames used to render chips from searches from the homepage visualization
       */
      visualizationFilters: ["villa", "center"],
      /**
       * Extra filter types that are used for legacy external provenance searches and to show
       * all deaccessioned works
       */
      otherFilters: [
        "has_provenance",
        "nazi_era_gaps",
        "nazi_era_restitution",
        "include_deaccessioned",
        "is_deaccessioned"
      ],
      /**
       * Object mapping ids to primary names for chip term values
       */
      chipTermIdValues: undefined,
      /**
       * the page title (hidden)
       */
      pageHeading: this.translate("search.heading")
    };
  },
  computed: {
    /**
     * Whether to sort the autocomplete by relevancy or alphabetically
     *
     * @returns {boolean}
     */
    useAlphabeticTypeaheadSort() {
      return configHelper.useAlphabeticTypeaheadSort();
    },
    /**
     * The current search query which will just be the same as the route in a desktop view, but
     * could represent the facets applied in a mobile overlay.
     *
     * @override
     * Override of baseSearchPage, passing -obj_num_sort
     * so as to keep it from passing to the query string
     */
    currentQuery() {
      return searchService.constructQueryObject(this.searchParams, {
        PAGE_SIZE: this.pageSize,
        SORT: "-obj_num_sort"
      });
    },
    /**
     * Data for loading state loading image and caption items
     * @returns {array}
     */
    loadingItemsData() {
      let loadingItems = [];
      for (let i = 0; i < this.pageSize; i++) {
        loadingItems.push({ id: i });
      }
      return loadingItems;
    },
    /**
     * Creates search term and filter chip objects for radAdvancedSearchBox and mobile overlay
     *
     * @returns {Array} array of chip term objects
     */
    chipTerms() {
      if (!this.chipTermIdValues) {
        // otherwise chip term displays the indexed id until primary name is fetched
        return [];
      }

      // This includes expected filters from the filterBar and from the
      // homepage data visualization
      let filterBarFilters = this.filterInfo.map((e) => e.fieldName);
      let potentialFilters = [
        ...filterBarFilters,
        ...this.visualizationFilters,
        ...this.otherFilters
      ];

      return searchHelpers.getChipObjects(
        this.searchParams.filters,
        potentialFilters,
        this.translatePart,
        this.chipTermIdValues
      );
    }
  },
  watch: {
    $route() {
      this.fetchData();
    },
    /**
     * Re-fetch data when search params update apart from route, as in mobile search
     */
    searchParams() {
      this.fetchData(this.searchParams);
    },
    /**
     * Watches the results to see if the first result matches the Accession Number and redirects the user to the object page
     */
    results() {
      // perform the keyword search, set the total for the facetExplorer, and
      // set whether we should display an error based the searchParams and total
      if (this.results) {
        this.total = this.results.total;
        this.displayError = !this.isEmptySearch && this.total > 0;
        this.isLoading = false;

        if (this.results.data) {
          let first = this.results.data[0];
          if (
            searchHelpers.accessionNumberMatches(this.searchParams.query, first)
          ) {
            this.$router.replace({
              name: "object",
              state: { indexedId: first.id, slug: first.id_manager_slug },
              params: { slug: first.id_manager_slug }
            });
          }
        }

        // update state of facets and filterbar
        this.updateFacetInfo();
        this.updateFilterInfo();
      }
    },
    /**
     * Watch the total and update the header/count visibility based on the total
     *
     * @param {string} prev
     * @param {string} incoming
     */
    total() {
      this.updateHeaderVisibility(this.total);
    }
  },
  created() {
    this.fetchData();
  },
  mounted() {
    useHead({
      title: metaTitle,
      meta: [
        { name: "robots", content: "noindex" },
        {
          name: "description",
          content: this.translatePart("search.metatag_description")
        }
      ]
    });
  },
  methods: {
    /**
     * Fetches the search results and facet data
     */
    async fetchData(searchParams = undefined) {
      if (!searchParams) {
        this.searchParams = this.getSearchParams(this.$route.query);
        searchParams = this.searchParams;
      }
      this.isLoading = true;
      this.chipTermIdValues = await this.getChipTermIdValues(searchParams);
      this.results = await this.fetchSearch(this.searchParams);

      this.updatePageTitle(this.pageTitle);
      metaTitle.value = this.pageTitle;

      // if auto-applying the facets, then, call updateActiveTabFilters fake a facet request
      this.updateActiveTabFilters({
        fieldName: this.activeTab,
        value: this.facetFilterPrefix,
        type: "keypressChange"
      });
    },
    /**
     * Parses current route query object and returns search parameters
     *
     * @param {object} queryObject - query object from current route
     *
     * @returns {object} searchParams - search parameters object
     */
    getSearchParams(queryObject) {
      let searchParams = searchService.extractSearchParams(
        queryObject,
        Object.keys(this.filterMap),
        this.pageSize
      );

      let nonBooleanParams = Object.keys(searchParams.filters).filter((field) =>
        searchService.getBooleanFilters(this.filterMap).indexOf(field)
      );

      // if there's no queries and no filters that aren't booleans, then we sort by object_number
      if (
        (searchParams.query == undefined || searchParams.query.length == 0) &&
        nonBooleanParams.length == 0
      ) {
        searchParams.sort = "-obj_num_sort";
      } else {
        // fallback to relevance
        delete searchParams.sort;
      }
      return searchParams;
    },
    /**
     * Wrapper for search item helper for Image and Caption component
     *
     * @param {object} result - search result hit object
     *
     * @returns {object} imageAndCaption attributes object
     */
    toGridItem(result) {
      return transformSearchResultToThumbnailItemWithArtist(
        result,
        true,
        this.translatePart
      );
    },
    /**
     * Updates isMobile when window is resized and toggles mobile overlay
     * if needed
     */
    onResize() {
      this.isMobile = this.isSmallMinus();
    },
    /**
     * Inserts <i> tags into the displayed suggestion string to show the type
     * that the match is (e.g. (Title), (Person))
     *
     * @param {String} text - The current (match) string
     * @param {Object} suggestionObject - The matched result's details
     *
     * @returns {String}
     */
    addSuffix(text, suggestionObject) {
      return autocompleteHelpers.addSuffix(
        text,
        suggestionObject,
        this.translatePart
      );
    },
    /** Event to be triggered instead of an autocomplete search event
     *
     * @param {Object} event - details of the selected suggestion that is passed
     * through the event
     */
    autocompleteEntry(event) {
      return autocompleteHelpers.autocompleteEntry(event, this.$router);
    },
    /**
     * the autocomplete classes
     * @returns {object} classes
     */
    classes() {
      const l = this.$route.query.q && this.$route.query.q.length == 1;
      const h =
        !this.currentSearch && l ? this.$route.query.q[0] : this.currentSearch;
      return { h: h == "\u0068\u006F\u0074\u0064\u006F\u0067" };
    }
  }
};
</script>

<style lang="scss">
@import "./index.scss";
</style>
